import { useCallback, useEffect, useState } from "react";
import { Card } from "../../../components";
import { Row } from "../../../components/row";
import { useToast } from "../../../components/toast-container";
import {
  COMMUNITY_IMPACT_MAP_KEY,
  LIVE_EVENTS_MAP_KEY,
  USER_DISTRIBUTIONS_MAP_KEY,
} from "../commons/Constant";
import Filters from "../components/Filters";
import { GeographicTabMenu } from "../components/geographic/GeographicTabMenu";
import CommunityImpactTab from "../components/geographic/tab-content/CommunityImpactTab";
import LiveEventsTab from "../components/geographic/tab-content/LiveEventsTab";
import UserDistributionTab from "../components/geographic/tab-content/UserDistributionTab";
import "../components/map/shared/Heatmap.css";
import Statistic from "../components/statistics/Statistic";
import AreaAnalysisTable from "../components/tables/AreaAnalysisTable";
import TargetOpportunityTable from "../components/tables/TargetOpportunityTable";
import { useAuthSession } from "../../auth/shared";
import { useDashboardOptions } from "../hooks/useDashboardOptions";
import { useHeatMapDashboard } from "../hooks/useHeatMapDashboard";
import { useOrganizationMapConfig } from "../hooks/useOrganizationMapConfig";
import type { HeatmapFilterModel } from "../types/Filter";

const EMPTY_FILTERS: HeatmapFilterModel = {
  neighborhoodIDs: [],
  demographicTypeIDs: [],
  categoryIDs: [],
  fromDate: undefined,
  toDate: undefined,
  eventName: undefined,
  eventStatus: undefined,
};

export default function HeatMapDashboard() {
  const [filters, setFilters] = useState<HeatmapFilterModel | null>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<HeatmapFilterModel | null>(EMPTY_FILTERS);
  const [displayMap, setDisplayMap] = useState(LIVE_EVENTS_MAP_KEY);
  const [filterRevision, setFilterRevision] = useState(0);
  const dashboard = useHeatMapDashboard(filters, { includeMerchants: false });
  const { addToast } = useToast();
  const { user } = useAuthSession();
  const organizationName = user?.organization_name ?? "CivicPoints";
  const mapConfig = useOrganizationMapConfig(user?.organization_id, user?.organization_map);
  const options = useDashboardOptions(user?.organization_id);
  const canAccessCommunityImpact = user?.role === "super_admin";

  useEffect(() => {
    if (!canAccessCommunityImpact && displayMap === COMMUNITY_IMPACT_MAP_KEY) {
      setDisplayMap(LIVE_EVENTS_MAP_KEY);
    }
  }, [canAccessCommunityImpact, displayMap]);

  const handleApplyFilters = useCallback((nextFilters: HeatmapFilterModel | null) => {
    setFilters(nextFilters);
    setDraftFilters(nextFilters);
    setFilterRevision((revision) => revision + 1);
  }, []);
  const handleEventSearch = useCallback((eventName: string) => {
    setFilters((current) => ({ ...(current ?? {}), eventName }));
    setDraftFilters((current) => ({ ...(current ?? {}), eventName }));
  }, []);
  const handleExportReport = useCallback(async () => {
    try {
      await dashboard.exportReport(dashboard.payload);
      addToast({
        type: "success",
        title: "Export started",
        message: "The report is downloading.",
      });
    } catch {
      addToast({
        type: "error",
        title: "Export failed",
        message: dashboard.exportError ?? "Failed to export report",
      });
    }
  }, [addToast, dashboard]);

  const handleRetryStatistics = useCallback(() => {
    void dashboard.statistics.refetch();
  }, [dashboard]);
  const handleRetryOptions = useCallback(() => {
    void options.refetch();
  }, [options]);
  const handleRetryAreas = useCallback(() => {
    void dashboard.heatMapAreas.refetch();
  }, [dashboard]);
  const handleRetryLiveEvents = useCallback(() => {
    void dashboard.liveEvents.refetch();
  }, [dashboard]);
  const handleRetryAreaAnalysis = useCallback(() => {
    void dashboard.areaAnalysis.refetch();
  }, [dashboard]);
  const handleRetryTargetOpportunities = useCallback(() => {
    void dashboard.targetOpportunities.refetch();
  }, [dashboard]);

  return (
    <div className="space-y-6">
      <div className="relative">
        <Card className="p-6 border-none rounded-none bg-white dark:bg-[#212936] border-gray-200 dark:border-gray-700">
          <Statistic
            data={dashboard.statistics.data}
            displayMap={displayMap}
            organizationName={organizationName}
            isLoading={dashboard.statistics.loading}
            error={dashboard.statistics.error}
            onRetry={handleRetryStatistics}
          />
          <Filters
            filters={draftFilters}
            onFiltersChange={setDraftFilters}
            neighborhoodOptions={options.neighborhoods}
            categoryOptions={options.categories}
            displayMap={displayMap}
            onDisplayMapChange={setDisplayMap}
            onApply={handleApplyFilters}
            onExport={handleExportReport}
            isExporting={dashboard.exportLoading}
            isOptionsLoading={options.loading}
            optionsError={options.error}
            onRetryOptions={handleRetryOptions}
          />
          <Row className="justify-between mb-4 mt-4">
            <div>
              <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Geographic Analytics
              </div>
              <div className="text-sm mt-2 text-[#4A5565] dark:text-gray-400">
                Monitor user distribution and live city events
              </div>
            </div>
            <div className="mb-1">
              <GeographicTabMenu
                value={displayMap}
                onChange={setDisplayMap}
                canAccessCommunityImpact={canAccessCommunityImpact}
              />
            </div>
          </Row>
          {displayMap === USER_DISTRIBUTIONS_MAP_KEY && (
            <UserDistributionTab
              areas={dashboard.heatMapAreas.data}
              organizationName={organizationName}
              mapConfig={mapConfig}
              isLoading={dashboard.heatMapAreas.loading}
              error={dashboard.heatMapAreas.error}
              onRetry={handleRetryAreas}
              filterRevision={filterRevision}
            />
          )}
          {displayMap === LIVE_EVENTS_MAP_KEY && (
            <LiveEventsTab
              areas={dashboard.liveEvents.data}
              organizationName={organizationName}
              mapConfig={mapConfig}
              activityLevels={dashboard.liveEvents.activityLevels}
              eventDetails={dashboard.liveEvents.eventDetails}
              eventStatus={filters?.eventStatus}
              isLoading={dashboard.liveEvents.loading}
              error={dashboard.liveEvents.error}
              onRetry={handleRetryLiveEvents}
              filterRevision={filterRevision}
              onSearchChange={handleEventSearch}
            />
          )}
          {canAccessCommunityImpact && displayMap === COMMUNITY_IMPACT_MAP_KEY && (
            <CommunityImpactTab
              areas={dashboard.heatMapAreas.data}
              analysis={dashboard.areaAnalysis.data}
              organizationName={organizationName}
              mapConfig={mapConfig}
              areasLoading={dashboard.heatMapAreas.loading}
              areasError={dashboard.heatMapAreas.error}
              onRetryAreas={handleRetryAreas}
              analysisLoading={dashboard.areaAnalysis.loading}
              analysisError={dashboard.areaAnalysis.error}
              onRetryAnalysis={handleRetryAreaAnalysis}
              filterRevision={filterRevision}
            />
          )}
          <Row className="mt-5">
            <AreaAnalysisTable
              data={dashboard.areaAnalysis.data}
              isLoading={dashboard.areaAnalysis.loading}
              error={dashboard.areaAnalysis.error}
              onRetry={handleRetryAreaAnalysis}
            />
          </Row>
          <Row className="mt-5">
            <TargetOpportunityTable
              data={dashboard.targetOpportunities.data}
              isLoading={dashboard.targetOpportunities.loading}
              error={dashboard.targetOpportunities.error}
              onRetry={handleRetryTargetOpportunities}
            />
          </Row>
        </Card>
      </div>
    </div>
  );
}
